import {withToken} from '@pexip/infinity-api';
import {logger, redact} from './logger';
import type {
    Client,
    EndpointResponse,
    ExtendedInfinityErrorMessage,
    GenerateEndpointParams,
    GetEndpointParams,
    GetEndpointResponse,
    InfinitySignals,
    RequestClient,
} from './types';
import {captureNoRequestClient, isCriticalAction} from './utils';

export const createEndpointManager = ({
    cleanupAndDisconnect,
    signals,
    getRequestClient,
}: {
    cleanupAndDisconnect: (reason: ExtendedInfinityErrorMessage) => void;
    signals: InfinitySignals;
    getRequestClient: () => RequestClient | undefined;
}) => {
    const props = {
        retryList: [] as Array<() => Promise<EndpointResponse>>,
    };

    function generateEndpoint<T extends keyof Client>(
        opts: GenerateEndpointParams<T>,
    ) {
        const wrappedFunc = async (
            ...args: GetEndpointParams<T>
        ): Promise<GetEndpointResponse<T> | undefined> => {
            try {
                for (const arg of args) {
                    redact(arg, 'token');
                    redact(arg, 'ssoToken');
                    redact(arg, 'registrationToken');
                }
            } catch (e) {
                logger.error(
                    {error: e},
                    'Error redacting sensitive information from request',
                );
            }
            const {func, fetcher, funcName, requiresToken, retriable} = opts;

            const flushRetryQueue = async () => {
                if (
                    props.retryList.length > 0 &&
                    (await getRequestClient()?.refreshToken())
                ) {
                    const promise = Promise.allSettled(
                        props.retryList.map(func => func()),
                    );
                    props.retryList = [];
                    const res = await promise;
                    const hasFailure = res.some(s => s.status === 'rejected');
                    if (!hasFailure) {
                        signals.onRetryQueueFlushed.emit();
                    }
                }
            };

            let endpointPromise: Awaited<GetEndpointResponse<T>> | undefined;
            try {
                if (requiresToken) {
                    const requestClient = getRequestClient();
                    if (!requestClient) {
                        captureNoRequestClient();
                        return;
                    }
                    await flushRetryQueue();
                    endpointPromise = await func(args, (input, init) => {
                        return withToken(fetcher, requestClient?.token ?? '')(
                            input,
                            init,
                        );
                    });
                } else {
                    endpointPromise = await func(args);
                }
            } catch (e) {
                if (retriable) {
                    logger.warn(
                        {error: e},
                        `Adding failed request '${funcName}' to retry queue.`,
                    );
                    props.retryList.push(() => wrappedFunc(...args));
                } else {
                    logger.error(
                        {error: e},
                        `Request '${funcName}' threw an Error.`,
                    );
                    if (isCriticalAction(funcName)) {
                        cleanupAndDisconnect(
                            'Could not execute critical network action',
                        );
                        return endpointPromise;
                    }
                }
                signals.onFailedRequest.emit(funcName);
                throw e;
            }
            return endpointPromise;
        };
        return wrappedFunc;
    }

    const cleanup = () => {
        props.retryList = [];
    };

    return {
        generateEndpoint,
        cleanup,
    };
};
